import { useEffect } from "react";
import { createPortal } from "react-dom";
import {
    FiEdit3,
    FiFilm,
    FiFolderPlus,
    FiImage,
    FiLoader,
    FiSave,
    FiTarget,
} from "react-icons/fi";

export type GenerationLoaderVariant =
    | "image"
    | "dental"
    | "dental-editor"
    | "video"
    | "case-load"
    | "case-create"
    | "case-save"
    | "default";

interface GenerationLoaderProps {
    loading: boolean;
    /** Preferred: stage-aware variant. Falls back to `text` when omitted. */
    variant?: GenerationLoaderVariant;
    /** Legacy single-line text when variant is not set */
    text?: string;
}

const VARIANT_COPY: Record<
    GenerationLoaderVariant,
    {
        title: string;
        subtitle: string;
        Icon: typeof FiImage;
        accent: string;
        ring: string;
        spin: string;
    }
> = {
    image: {
        title: "Generating smile design…",
        subtitle: "Creating your approved after smile from the patient photo.",
        Icon: FiImage,
        accent: "text-[#1DA8C6]",
        ring: "border-[#2fa9bf]/20",
        spin: "border-t-[#2fa9bf]",
    },
    dental: {
        title: "Refining dental accuracy…",
        subtitle: "Cleaning seams and lighting while keeping your tooth edits.",
        Icon: FiTarget,
        accent: "text-violet-300",
        ring: "border-violet-400/20",
        spin: "border-t-violet-400",
    },
    "dental-editor": {
        title: "Opening tooth editor…",
        subtitle:
            "Detecting teeth on the after smile. This can take a moment the first time.",
        Icon: FiEdit3,
        accent: "text-[#1DA8C6]",
        ring: "border-[#2fa9bf]/20",
        spin: "border-t-[#2fa9bf]",
    },
    video: {
        title: "Generating smile video…",
        subtitle: "Building an 8-second clip with head movement and a full smile.",
        Icon: FiFilm,
        accent: "text-emerald-300",
        ring: "border-emerald-400/20",
        spin: "border-t-emerald-400",
    },
    "case-load": {
        title: "Loading smile case…",
        subtitle: "Fetching case details, images, and saved progress.",
        Icon: FiLoader,
        accent: "text-[#1DA8C6]",
        ring: "border-[#2fa9bf]/20",
        spin: "border-t-[#2fa9bf]",
    },
    "case-create": {
        title: "Creating smile case…",
        subtitle: "Setting up the case folder and workspace.",
        Icon: FiFolderPlus,
        accent: "text-amber-300",
        ring: "border-amber-400/20",
        spin: "border-t-amber-400",
    },
    "case-save": {
        title: "Saving smile case…",
        subtitle: "Uploading images and syncing this case.",
        Icon: FiSave,
        accent: "text-[#1DA8C6]",
        ring: "border-[#2fa9bf]/20",
        spin: "border-t-[#2fa9bf]",
    },
    default: {
        title: "Working…",
        subtitle: "Please wait a moment.",
        Icon: FiLoader,
        accent: "text-[#1DA8C6]",
        ring: "border-[#2fa9bf]/20",
        spin: "border-t-[#2fa9bf]",
    },
};

export default function GenerationLoader({
    loading,
    variant,
    text,
}: GenerationLoaderProps) {
    useEffect(() => {
        if (!loading) return;

        const scrollContainer = document.querySelector<HTMLElement>(
            ".smile-studio-scroll-root"
        );
        const previousOverflow = scrollContainer?.style.overflow;

        if (scrollContainer) {
            scrollContainer.style.overflow = "hidden";
        }

        return () => {
            if (scrollContainer) {
                scrollContainer.style.overflow = previousOverflow ?? "";
            }
        };
    }, [loading]);

    if (!loading) return null;

    const resolved: GenerationLoaderVariant = variant ?? "default";
    const copy = VARIANT_COPY[resolved];
    const { Icon } = copy;
    const title = text && !variant ? text : copy.title;
    const subtitle = variant ? copy.subtitle : undefined;

    return createPortal(
        <div
            className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label={title}
        >
            <div className="relative flex flex-col items-center max-w-sm text-center">
                <div className="relative w-16 h-16 sm:w-24 sm:h-24">
                    <div
                        className={`absolute inset-0 rounded-full border-4 ${copy.ring} ${copy.spin} animate-spin`}
                    />
                    <Icon
                        className={`absolute inset-0 m-auto w-6 h-6 sm:w-8 sm:h-8 ${copy.accent}`}
                        aria-hidden
                    />
                </div>

                <p className="mt-4 sm:mt-6 text-gray-100 text-sm sm:text-base font-semibold px-4">
                    {title}
                </p>
                {subtitle && (
                    <p className="mt-2 text-[#8b8f8d] text-xs sm:text-sm px-4 leading-relaxed">
                        {subtitle}
                    </p>
                )}
            </div>
        </div>,
        document.body
    );
}
